import DragZoom from 'ol/interaction/DragZoom';
import GeojsonLayer from '@apps-common/ui-maps/GeojsonLayer.jsx';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import Map from '@planet/maps/Map';
import MapboxLayer from '@planet/maps/layer/MapboxVector';
import Overlay from '@planet/maps/Overlay';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import TileLayer from '@planet/maps/layer/WebGLTile';
import View from '@planet/maps/View';
import XYZSource from '@planet/maps/source/XYZ';
import {always} from 'ol/events/condition';
import {array, bool, func, number, object, string} from 'prop-types';
import {connect, useSelector} from 'react-redux';
import {defaults as defaultInteractions} from 'ol/interaction/defaults';
import {useAuth} from '@apps-common/auth-react';

import CenterMarker from './center-marker.jsx';
import CompareTileLayers from './compare-layers.jsx';
import EditableVectorLayer from './editable-vector-layer.jsx';
import RasterTileLayers from './raster-layer.jsx';
import WatermarkLayer from './watermark-layer.jsx';
import {
  BASE_LAYER_URL,
  LABELS_STYLE_URL,
  MAPBOX_ACCESS_TOKEN,
  STILES_LAYER_URL,
} from '@explorer/config';
import {
  ENHANCEMENT_MODE,
  MAP_DRAW_LAYERS,
  MAP_TOOLS,
  MAX_FOLDERS_ZOOM,
  MAX_ZOOM,
  TILE_MODE,
  TILE_MODE_QUOTA,
  UI_MODES,
} from '@explorer/constants';
import {LEFT_DRAWER_WIDTH, RIGHT_DRAWER_WIDTH} from '@explorer/styles/mixins';
import {
  changeMapView,
  fitExtent,
  pinMap,
  setDrawTool,
} from '@explorer/actions/map';
import {
  consumeTileQuota,
  setCurrentSubscriptions,
} from '@explorer/actions/subscriptions';
import {createGeometriesMask} from '@explorer/util/map';
import {defineGeometry} from '@explorer/actions/search';
import {
  footprintStyle,
  mosaicGeometryStyle,
} from '@explorer/util/map-edit-styles';
import {
  getCompareSubjectsConfigs,
  getEnhanceFilters,
  getTileConfigs,
  isImageryDownsampled,
  quotaZoomCheck,
} from '@explorer/selectors/tile-groups';
import {getCurrentSubIds} from '@explorer/selectors/subscriptions.js';
import {getExtent} from '@explorer/util/geom';
import {
  getGeometryZoomTo,
  getSearchGeometryGeoJson,
} from '@explorer/selectors/search';
import {getMosaicAOIs} from '@explorer/selectors/mosaics';
import {isAnalytic} from '@explorer/selectors/capabilities';

const dragZoomInteraction = new DragZoom({condition: always});

const ViewerMap = ({
  boxZoomEnabled,
  center,
  changeMapView,
  compareMethod,
  compareModeValues,
  consumeTileQuota,
  defineGeometry,
  drawTool,
  enhanceFilters,
  enhancementOn,
  fitExtent,
  focusedItems,
  hoverFeatures,
  isAnalytic,
  isComparing,
  isLeftDrawerOpen,
  isRightDrawerOpen,
  mapNextExtent,
  mapProps,
  measureFeatures,
  mosaicAois,
  overlays,
  pinMap,
  pinnedLocation,
  quickFocusedItems,
  searchFeatures,
  searchGeometry,
  searchGeometryId,
  setCurrentSubscriptions,
  setDrawTool,
  setMeasureFeatures,
  setSearchFeatures,
  setTempGeometry,
  tileMode,
  zoom,
  zoomToSearchGeometry,
}) => {
  const auth = useAuth();

  const viewRef = useRef(null);
  const mapRef = useRef(null);
  const [pinElement, setPinElement] = useState();

  const compareConfigs = useSelector(state =>
    getCompareSubjectsConfigs(state, auth)
  );
  const tileConfigs = useSelector(state => getTileConfigs(state, auth));
  const isOverZoomed = useSelector(state => quotaZoomCheck(state, auth));

  const showWatermark = useSelector(state => {
    const currentSubIds = getCurrentSubIds(state);
    const imageryIsDownsampled = isImageryDownsampled(state, auth);

    if (
      !tileConfigs?.[tileMode] ||
      (tileMode === TILE_MODE.RESULTS && !currentSubIds.length)
    ) {
      return false;
    }

    if (tileMode === TILE_MODE.BASEMAPS) {
      return !isOverZoomed;
    } else if (tileMode === TILE_MODE.RESULTS) {
      return !isOverZoomed || imageryIsDownsampled;
    }
    return false;
  });

  // when the geometry changes, reproject it to map coordinates
  const [viewState, setViewState] = useState({
    center: [0, 0],
    zoom: 1,
  });

  useEffect(() => {
    setViewState({center, zoom});
  }, [center, zoom]);

  const onViewChange = useCallback(
    event => {
      const view = event.target;
      const newCenter = view.getCenter();
      const newZoom = view.getZoom();
      changeMapView({
        center: newCenter,
        zoom: newZoom,
      });
    },
    [changeMapView]
  );

  const onMapSingleClick = useCallback(
    event => {
      pinMap({coordinate: event.coordinate});
    },
    [pinMap]
  );

  const zoomToExtent = useCallback(
    extent => {
      const buffer = 16;
      const padding = [
        buffer,
        (isRightDrawerOpen ? RIGHT_DRAWER_WIDTH : 0) + buffer,
        buffer,
        (isLeftDrawerOpen ? LEFT_DRAWER_WIDTH : 0) + buffer,
      ];

      return new Promise(resolve => {
        // 100ms gives the layer a chance to redraw
        //  OL will not redraw while zooming.
        setTimeout(() => {
          viewRef.current.fit(extent, {
            padding,
            duration: 800,
          });
        }, 100);

        resolve();
      });
    },
    [isLeftDrawerOpen, isRightDrawerOpen]
  );

  useEffect(() => {
    if (zoomToSearchGeometry && searchGeometry) {
      const extent = getExtent(searchGeometry);
      zoomToExtent([extent.left, extent.bottom, extent.right, extent.top]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchGeometry, zoomToSearchGeometry]);

  useEffect(() => {
    if (searchGeometry) {
      setSearchFeatures([
        {
          type: 'Feature',
          properties: {},
          id: searchGeometryId,
          geometry: searchGeometry,
        },
      ]);
    } else if (searchFeatures?.length > 0 && !searchGeometry) {
      setSearchFeatures([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchGeometryId, searchGeometry]);

  useEffect(() => {
    if (mapNextExtent.length === 4) {
      zoomToExtent(mapNextExtent).then(() => {
        fitExtent([]);
      });
    }
  }, [mapNextExtent, fitExtent, zoomToExtent]);

  const consumeQuota = useCallback(
    image => {
      if (tileMode !== TILE_MODE.FOLDERS) {
        const planetSubs = image.headers.get('x-planet-sub');
        // get the list of subscriptions from the string.
        //  Subscriptions should only be integers.
        //  NaN is false-y and is removed with the filter.
        const subIds = planetSubs
          ?.split(',')
          .map(v => parseInt(v))
          .filter(v => !!v);

        if (subIds?.length > 0) {
          setCurrentSubscriptions(subIds);
          if (isOverZoomed) {
            const quotaType = TILE_MODE_QUOTA[tileMode];

            // remove a tile from each subscription.
            consumeTileQuota({
              subId: subIds,
              type: quotaType,
            });
          }
        }
      }
    },
    [tileMode, consumeTileQuota, setCurrentSubscriptions, isOverZoomed]
  );

  const onAddGeometryFeature = useCallback(
    newFeature => {
      if (newFeature) {
        setSearchFeatures([newFeature]);
        defineGeometry({geometry: newFeature.geometry});
        // reset selection
        setDrawTool({
          name: MAP_TOOLS.NONE,
          layer: MAP_DRAW_LAYERS.NONE,
          target: '',
        });
      }
    },
    [defineGeometry, setDrawTool, setSearchFeatures]
  );

  const onAddMeasureFeature = useCallback(
    newFeature => {
      setMeasureFeatures(measureFeatures.concat([newFeature]));
    },
    [measureFeatures, setMeasureFeatures]
  );

  useEffect(() => {
    const resetBoxZoom = () => {
      setDrawTool({
        name: MAP_TOOLS.NONE,
        layer: MAP_DRAW_LAYERS.NONE,
        target: '',
      });
    };
    dragZoomInteraction.on('boxend', resetBoxZoom);

    return () => {
      dragZoomInteraction.un('boxend', resetBoxZoom);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dragZoomInteraction.setActive(boxZoomEnabled);
  }, [boxZoomEnabled, setDrawTool]);

  useEffect(() => {
    if (mapRef.current) {
      let cursor = 'default';
      if (drawTool.name !== MAP_TOOLS.NONE) {
        cursor = 'crosshair';
      }
      mapRef.current.getTarget().style.cursor = cursor;
    }
  }, [drawTool]);

  return (
    <React.Fragment>
      <Map
        controls={[]}
        onSingleClick={onMapSingleClick}
        {...mapProps}
        ref={node => {
          mapRef.current = node;
          mapProps.ref(node);
        }}
        interactions={defaultInteractions().extend([dragZoomInteraction])}
      >
        <View
          ref={viewRef}
          center={viewState.center}
          zoom={viewState.zoom}
          options={{
            maxZoom:
              tileMode === TILE_MODE.FOLDERS ? MAX_FOLDERS_ZOOM : MAX_ZOOM,
          }}
          onChange={onViewChange}
        />

        <MapboxLayer
          visible={!overlays.stiles}
          options={{
            accessToken: MAPBOX_ACCESS_TOKEN,
            styleUrl: BASE_LAYER_URL,
          }}
          onSourceReady={event => {
            event.target
              .getSource()
              .setAttributions([
                '©<a href="https://www.mapbox.com/about/maps/" target="_blank">Mapbox</a>',
                '©<a href="http://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>',
              ]);
          }}
        />

        <TileLayer visible={overlays.stiles}>
          <XYZSource
            url={STILES_LAYER_URL}
            options={{
              crossOrigin: 'anonymous',
              attributions: ['©2018 Planet Labs PBC'],
            }}
          />
        </TileLayer>

        <RasterTileLayers
          olView={viewRef}
          enhancementOn={enhancementOn}
          clipGeometry={tileMode === TILE_MODE.RESULTS ? searchGeometry : null}
          rasterConfigs={tileConfigs[tileMode]?.rasterConfigs || []}
          numpyUrl={tileConfigs[tileMode]?.numpyUrl}
          pixelDepth={tileConfigs[tileMode]?.pixelDepth}
          isAnalytic={isAnalytic}
          enhanceFilters={enhanceFilters}
          onTileLoad={consumeQuota}
          visible={!isComparing}
        />

        <CompareTileLayers
          visible={isComparing}
          compareTileConfigs={compareConfigs}
          clipGeometry={searchGeometry}
          olMapRef={mapProps.ref}
          onTileLoad={consumeQuota}
          values={compareModeValues}
          compareMethod={compareMethod}
        />

        <GeojsonLayer
          style={mosaicGeometryStyle}
          visible={tileMode === TILE_MODE.BASEMAPS}
          features={mosaicAois}
        />

        <GeojsonLayer
          style={footprintStyle}
          visible={!isComparing}
          features={focusedItems.map(item => item.geometry)}
        />

        <GeojsonLayer
          style={footprintStyle}
          visible={!isComparing}
          features={quickFocusedItems.map(item => item.geometry)}
        />

        <EditableVectorLayer
          layerName={MAP_DRAW_LAYERS.GEOMETRY}
          features={searchFeatures}
          drawTool={drawTool}
          onAddFeature={onAddGeometryFeature}
          setTempGeometry={setTempGeometry}
          visible={tileMode !== TILE_MODE.FOLDERS}
        />

        <EditableVectorLayer
          layerName={MAP_DRAW_LAYERS.MEASURE}
          features={measureFeatures}
          drawTool={drawTool}
          onAddFeature={onAddMeasureFeature}
          setTempGeometry={setTempGeometry}
        />

        <GeojsonLayer
          style="hover"
          visible={drawTool.name !== MAP_TOOLS.EDIT}
          features={hoverFeatures}
        />

        <MapboxLayer
          visible={overlays.labels}
          options={{
            accessToken: MAPBOX_ACCESS_TOKEN,
            styleUrl: LABELS_STYLE_URL,
          }}
          onSourceReady={event => {
            event.target
              .getSource()
              .setAttributions([
                '©<a href="https://www.mapbox.com/about/maps/" target="_blank">Mapbox</a>',
                '©<a href="http://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>',
              ]);
          }}
        />

        <WatermarkLayer
          visible={showWatermark}
          clipGeometry={
            tileMode === TILE_MODE.BASEMAPS
              ? createGeometriesMask(mosaicAois)
              : tileMode === TILE_MODE.RESULTS
                ? searchGeometry
                : null
          }
        />
        <CenterMarker
          geometry={searchGeometry}
          visible={overlays.centerMarker}
        />
        <Overlay
          element={pinElement}
          position={pinnedLocation}
          positioning="center-center"
        />
      </Map>
      <GpsFixedIcon
        ref={setPinElement}
        fontSize="small"
        sx={{filter: 'drop-shadow( 0 0px 2px rgba(0, 0, 0, .3))'}}
      />
    </React.Fragment>
  );
};

ViewerMap.propTypes = {
  boxZoomEnabled: bool,
  center: array,
  changeMapView: func,
  compareConfigs: array,
  compareMethod: string,
  compareModeValues: object,
  compareSliderValue: number,
  consumeTileQuota: func,
  defineGeometry: func,
  drawTool: object,
  enhanceFilters: object,
  enhancementOn: bool,
  fitExtent: func,
  focusedItems: array,
  hoverFeatures: array,
  isAnalytic: bool,
  isComparing: bool,
  isLeftDrawerOpen: bool,
  isRightDrawerOpen: bool,
  mapNextExtent: array,
  mapProps: object,
  measureFeatures: array,
  mosaicAois: array,
  overlays: object,
  pinMap: func,
  pinnedLocation: array,
  quickFocusedItems: array,
  searchFeatures: array,
  searchGeometry: object,
  searchGeometryId: number,
  setCurrentSubscriptions: func,
  setDrawTool: func,
  setMeasureFeatures: func,
  setSearchFeatures: func,
  setTempGeometry: func,
  tileConfigs: object,
  tileMode: string,
  zoom: number,
  zoomToSearchGeometry: bool,
};

const mapStateToProps = state => ({
  boxZoomEnabled: state.map.drawTool.name === MAP_TOOLS.BOX_ZOOM,
  center: state.map.center,
  // compareConfigs: getCompareSubjectsConfigs(state),
  compareMethod: state.compare.method,
  drawTool: state.map.drawTool,
  enhanceFilters: getEnhanceFilters(state),
  enhancementOn:
    state.tiles.modeSettings[state.tiles.mode].enhanceMode !==
    ENHANCEMENT_MODE.OFF,
  focusedItems: state.focusedItems,
  isAnalytic: isAnalytic(state),
  isComparing: state.ui.mode === UI_MODES.COMPARE,
  isLeftDrawerOpen: state.ui.drawers.left !== '',
  isRightDrawerOpen: state.ui.drawers.right !== '',
  mapNextExtent: state.map.fitExtent,
  mosaicAois: getMosaicAOIs(state),
  overlays: state.map.overlays,
  pinnedLocation: state.map.mapPin,
  quickFocusedItems: state.quickFocusedItems,
  searchGeomeryId: state.search.geometryId,
  searchGeometry: getSearchGeometryGeoJson(state),
  searchGeometryId: state.search.geometryId,
  tileMode: state.tiles.mode,
  zoom: state.map.zoom,
  zoomToSearchGeometry: getGeometryZoomTo(state),
});

const mapDispatchToProps = {
  changeMapView,
  fitExtent,
  consumeTileQuota,
  setCurrentSubscriptions,
  defineGeometry,
  setDrawTool,
  pinMap,
};

export default connect(mapStateToProps, mapDispatchToProps)(ViewerMap);
